import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Phone, Linkedin, MapPin, CheckCircle2 } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { SITE, API } from "@/siteConfig";

const SIZES = ["1–20", "20–100", "100–250", "250+"];
const TOPICS = [
  "Executive Coaching",
  "Fractional COO",
  "PMO & Transformation",
  "AI Transformation",
  "Not sure yet",
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  role: "",
  company_size: "",
  help_with: "",
  message: "",
  preferred_contact: "Call",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      toast.success("Message sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please email us directly at " + SITE.email);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 50% 40% at 85% 10%, rgba(43,217,199,0.08), transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid gap-16 lg:grid-cols-12">
            {/* LEFT — copy + details */}
            <div className="lg:col-span-5">
              <Reveal>
                <p className="overline-label mb-8">Contact</p>
              </Reveal>
              <h1
                className="font-serif text-4xl leading-[1.05] tracking-tighter text-white sm:text-5xl md:text-6xl"
                data-testid="contact-headline"
              >
                <MaskedLines
                  lines={[
                    "Let's Talk About",
                    <span key="t" className="italic text-teal">Your Turning Point</span>,
                  ]}
                />
              </h1>
              <Reveal delay={0.25}>
                <p className="mt-8 max-w-md font-sans text-base leading-relaxed text-white/60 md:text-lg">
                  Every engagement starts with a conversation — no obligation, no canned pitch.
                  Tell us where you are, and we'll tell you honestly whether and how we can help.
                </p>
              </Reveal>

              <Reveal delay={0.35}>
                <ul className="mt-12 space-y-5 border-t border-white/10 pt-10 font-sans text-sm">
                  <li>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="contact-email"
                      className="flex items-center gap-4 text-white/70 transition-colors duration-300 hover:text-teal"
                    >
                      <Mail className="h-4 w-4 shrink-0 text-teal" /> {SITE.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={SITE.phoneHref}
                      data-testid="contact-phone"
                      className="flex items-center gap-4 text-white/70 transition-colors duration-300 hover:text-teal"
                    >
                      <Phone className="h-4 w-4 shrink-0 text-teal" /> {SITE.phone}
                    </a>
                  </li>
                  <li>
                    <a
                      href={SITE.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-linkedin"
                      className="flex items-center gap-4 text-white/70 transition-colors duration-300 hover:text-teal"
                    >
                      <Linkedin className="h-4 w-4 shrink-0 text-teal" /> {SITE.founder} — {SITE.founderRole}
                    </a>
                  </li>
                  <li className="flex items-center gap-4 text-white/40">
                    <MapPin className="h-4 w-4 shrink-0 text-teal/60" /> Based in Canada
                  </li>
                </ul>
              </Reveal>

              <Reveal delay={0.45}>
                <p className="mt-10 font-sans text-xs leading-relaxed text-white/40">
                  Working with growing companies across technology, professional services,
                  engineering, and defence.
                </p>
              </Reveal>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-7">
              {sent ? (
                <div
                  className="flex h-full min-h-[420px] flex-col items-start justify-center border border-teal/30 bg-navy-deep p-10 md:p-14"
                  data-testid="contact-success"
                >
                  <CheckCircle2 className="h-10 w-10 text-teal" />
                  <h2 className="mt-6 font-serif text-3xl text-white">Message received.</h2>
                  <p className="mt-4 max-w-md font-sans text-base leading-relaxed text-white/60">
                    Thank you, {form.name.split(" ")[0]}. Your note is on its way to {SITE.founder} —
                    expect a reply within one business day.
                  </p>
                  <button
                    onClick={() => { setForm(initialForm); setSent(false); }}
                    data-testid="contact-send-another"
                    className="btn-ghost mt-8"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <Reveal delay={0.2}>
                  <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                    <div className="grid gap-8 sm:grid-cols-2">
                      <div>
                        <label className="field-label" htmlFor="name">Name *</label>
                        <input
                          id="name"
                          required
                          value={form.name}
                          onChange={set("name")}
                          className="field-input"
                          placeholder="Your full name"
                          data-testid="contact-field-name"
                        />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="email">Email *</label>
                        <input
                          id="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={set("email")}
                          className="field-input"
                          placeholder="you@company.com"
                          data-testid="contact-field-email"
                        />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="company">Company</label>
                        <input
                          id="company"
                          value={form.company}
                          onChange={set("company")}
                          className="field-input"
                          placeholder="Company name"
                          data-testid="contact-field-company"
                        />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="role">Role / Title</label>
                        <input
                          id="role"
                          value={form.role}
                          onChange={set("role")}
                          className="field-input"
                          placeholder="e.g., CEO, VP Operations"
                          data-testid="contact-field-role"
                        />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="company_size">Company size</label>
                        <select
                          id="company_size"
                          value={form.company_size}
                          onChange={set("company_size")}
                          className="field-input field-select appearance-none"
                          data-testid="contact-field-size"
                        >
                          <option value="">Select…</option>
                          {SIZES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="field-label" htmlFor="help_with">What do you need help with?</label>
                        <select
                          id="help_with"
                          value={form.help_with}
                          onChange={set("help_with")}
                          className="field-input field-select appearance-none"
                          data-testid="contact-field-topic"
                        >
                          <option value="">Select…</option>
                          {TOPICS.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="field-label" htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        className="field-input resize-none"
                        placeholder="Tell us where you are — and where you need to go."
                        data-testid="contact-field-message"
                      />
                    </div>

                    <div>
                      <span className="field-label">Preferred way to connect</span>
                      <div className="flex gap-3" data-testid="contact-field-preference">
                        {["Call", "Email"].map((p) => (
                          <button
                            key={p}
                            type="button"
                            onClick={() => setForm({ ...form, preferred_contact: p })}
                            data-testid={`contact-preference-${p.toLowerCase()}`}
                            className={`rounded-full border px-6 py-2.5 font-sans text-sm transition-colors duration-300 ${
                              form.preferred_contact === p
                                ? "border-teal bg-teal/10 text-teal"
                                : "border-white/20 text-white/60 hover:border-white/40"
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={sending}
                      data-testid="contact-submit"
                      className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {sending ? "Sending…" : "Book a Call"}
                      {!sending && <ArrowUpRight className="h-4 w-4" />}
                    </button>
                    <p className="font-sans text-xs text-white/40">
                      By sending this form, you agree to our{" "}
                      <Link
                        to="/privacy"
                        data-testid="contact-privacy-link"
                        className="underline decoration-white/30 underline-offset-4 transition-colors duration-300 hover:text-teal"
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </form>
                </Reveal>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
